import TenantInvoicePosition, { InvoicePositionDistributionRule, InvoicePositionType } from "./TenantInvoicePosition"
import { getTranslation } from "utils/translations"
import { Purpose } from "config/constants"
import { OfferPurpose } from "./Offer"
import i18n from "intl/i18n"
import { calculateInvoicePositionAmount, InvoicePositionUnit, isQuantityRequiredForInvoicePositionUnit } from "./InvoicePositionBase"

export interface IGetOfferInvoicePositionDTO {
    unit: InvoicePositionUnit
    unitPrice: number
    unitQuantity?: number
    number: string
    distributionRule: InvoicePositionDistributionRule
    type: InvoicePositionType
    text?: string | null
}

export interface IOfferInvoicePosition extends Omit<IGetOfferInvoicePositionDTO, "unit" | "unitPrice" | "type" | "distributionRule"> {
    unit?: InvoicePositionUnit
    unitPrice?: number
    type?: InvoicePositionType | null
    distributionRule?: InvoicePositionDistributionRule
}

export default class OfferInvoicePosition {
    unit?: InvoicePositionUnit
    unitPrice?: number
    unitQuantity?: number
    number: string
    #distributionRule?: InvoicePositionDistributionRule
    customText?: string
    type?: InvoicePositionType
    #purpose: OfferPurpose

    // hydrated separetely
    invoicePosition?: TenantInvoicePosition

    constructor(dto: IOfferInvoicePosition, { purpose }: { purpose: OfferPurpose }) {
        this.unit = dto.unit
        this.unitPrice = dto.unitPrice
        this.unitQuantity = dto.unitQuantity
        this.number = dto.number
        this.#distributionRule = dto.distributionRule
        this.customText = typeof dto.text === "string" ? dto.text : undefined
        this.type = purpose === Purpose.sale ? InvoicePositionType.single : (dto.type ?? undefined)
        this.#purpose = purpose
    }

    toDto(): IGetOfferInvoicePositionDTO {
        return {
            unit: this.unit!,
            unitPrice: this.unitPrice!,
            unitQuantity: isQuantityRequiredForInvoicePositionUnit(this.unit!) ? this.unitQuantity : undefined,
            number: this.number,
            distributionRule: this.distributionRule!,
            type: this.type ?? InvoicePositionType.single,
            text: this.customText || null,
        }
    }

    get amount(): number | null {
        return calculateInvoicePositionAmount({
            unit: this.unit,
            unitPrice: this.unitPrice,
            unitQuantity: this.unitQuantity,
        })
    }

    get distributionRule() {
        return this.#distributionRule || this.invoicePosition?.distributionRule
    }

    get isHydrated() {
        return !!this.invoicePosition
    }

    get description(): string {
        if (!this.invoicePosition?.description) return ""
        return getTranslation(this.invoicePosition.description)
    }

    get text(): string {
        if (typeof this.customText === "string") return this.customText
        if (!this.invoicePosition?.text) return ""
        return getTranslation(this.invoicePosition.text)
    }

    set text(text: string | null | undefined) {
        this.customText = text || ""
    }

    get isSale(): boolean {
        return this.#purpose === Purpose.sale
    }

    get isWithType(): boolean {
        return !this.isSale
    }

    get isValid(): boolean {
        return this.amount !== null && isFinite(this.amount) && !!this.type
    }
}


export function getOfferInvoicePositionTypeLabel(offerInvoicePositionType: InvoicePositionType) {
    return {
        [InvoicePositionType.single]: i18n.t('reservationInvoicePositionType.single'),
        [InvoicePositionType.recurring]: i18n.t('reservationInvoicePositionType.recurring'),
    }[offerInvoicePositionType] || offerInvoicePositionType
}
